import cogoToast from 'cogo-toast';
import { uniq } from 'lodash';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  isOfferSuccessfull,
  LouiseOffer,
  QuoteGuarantee,
} from '../../../../../../types/Quotes';
import { isSocialLoginUrl } from '../../../../../app/src/features/authentication/constants/SocialAuth';
import { OfferState } from '../../../services/offersService';
import {
  getGuaranteeDescriptionForKey,
  getGuaranteeNameForKey,
} from '../../../services/productFactoryService';
import { useLouiseSelector } from '../../../store/redux/store';
import OfferOptionButton from './OfferOptionButton';

interface Props {
  offer: LouiseOffer;
  onOptionsChange: (arg0: string[]) => void;
}

const OfferOptionContainer: React.FC<React.PropsWithChildren<Props>> = ({
  offer,
  onOptionsChange,
}) => {
  const { t } = useTranslation();

  const activeCarQuoteFilter = useLouiseSelector(
    (state) => state.offers.activeCarQuoteFilter,
  );
  const selectedFlowGuarantees = useLouiseSelector(
    (state) => state.selection.selectedGuarantees,
  );
  const selectedOfferGuarantees: string[] =
    useLouiseSelector(
      (state) =>
        state.offers.offerSelectedGuarantees[offer.info.insurance_type]?.[
          activeCarQuoteFilter
        ]?.[offer.info.company.key],
    ) || offer.selected_guarantees;

  const [initialGuarantees, setInitialGuarantees] =
    useState<QuoteGuarantee[]>();

  // HARDCODED SOLUTION AS BE HAS BAD DESIGN FOR THIS
  const possibleGuarantees: string[] = (() => {
    if (offer.info.insurance_type === 'home') {
      const hardcodedGuarantees = [];
      if (selectedFlowGuarantees.home?.includes('FIRE') || isSocialLoginUrl) {
        hardcodedGuarantees.push('fire');
      }
      if (
        selectedFlowGuarantees.home?.includes('CONTENT') ||
        isSocialLoginUrl
      ) {
        hardcodedGuarantees.push('content');
      }

      return uniq([...hardcodedGuarantees, ...offer.possible_guarantees]);
    }
    return offer.possible_guarantees;
  })();

  useEffect(() => {
    if (offer.state === OfferState.FETCHED && isOfferSuccessfull(offer)) {
      // Sets the initial values if the quote was ever retrieved
      setInitialGuarantees(offer.guarantees);
    }
  }, [offer]);

  const checkMandatoryForHomeInsurance = (guarantee: string) => {
    if (
      ['fire', 'content'].includes(guarantee) &&
      offer.info.company.key !== 'villasure'
    ) {
      if (
        offer.selected_guarantees.includes('content') &&
        offer.selected_guarantees.includes('fire')
      ) {
        return false;
      } else if (
        !offer.selected_guarantees.includes('content') &&
        offer.selected_guarantees.includes('fire') &&
        guarantee === 'fire'
      ) {
        return true;
      } else if (
        offer.selected_guarantees.includes('content') &&
        !offer.selected_guarantees.includes('fire') &&
        guarantee === 'content'
      ) {
        return true;
      }
      return false;
    }
    return initialGuarantees?.find(({ name }) => name === guarantee)?.mandatory;
  };

  const handleOptionClick = (guarantee: string, isActive?: boolean) => {
    if (offer.info.insurance_type === 'home') {
      if (checkMandatoryForHomeInsurance(guarantee))
        return cogoToast.error(
          t('SALES-CONVERSATION_OFFERS_SINGLE-OFFER_GUARANTEE_MANDATORY'),
        );
    } else if (
      initialGuarantees?.find(({ name }) => name === guarantee)?.mandatory ||
      /.*omnium.*/.test(guarantee)
    )
      return cogoToast.error(
        t('SALES-CONVERSATION_OFFERS_SINGLE-OFFER_GUARANTEE_MANDATORY'),
      );

    let newGuarantees: string[];

    if (isActive)
      newGuarantees = selectedOfferGuarantees.filter(
        (guaranteeItem) => guarantee !== guaranteeItem,
      );
    else newGuarantees = uniq([...selectedOfferGuarantees, guarantee]);

    // Hardcoded check for legal and theft for AXA
    // TEMP
    if (
      offer.info.company.key === 'axa' &&
      offer.info.insurance_type === 'home' &&
      (guarantee === 'theft' || guarantee === 'legal')
    ) {
      if (isActive)
        newGuarantees = newGuarantees.filter(
          (guarantee) => guarantee !== 'legal' && guarantee !== 'theft',
        );
      else newGuarantees = uniq([...selectedOfferGuarantees, 'legal', 'theft']);
    }

    onOptionsChange(newGuarantees);
  };

  return (
    <OptionContainer>
      {possibleGuarantees.map((guarantee) => {
        const active = selectedOfferGuarantees.includes(guarantee);

        return (
          <OfferOptionButton
            key={`option_${guarantee}`}
            type={guarantee}
            active={active}
            onClick={() => handleOptionClick(guarantee, active)}
            disabled={offer.state === OfferState.LOADING}
            insuranceType={offer.info.insurance_type}
            description={getGuaranteeDescriptionForKey(
              guarantee,
              offer?.guarantee_content,
            )}
            price={
              offer.guarantees?.find(({ name }) => name === guarantee)
                ?.total_premium
            }
          >
            {getGuaranteeNameForKey(guarantee, offer?.guarantee_content)}
          </OfferOptionButton>
        );
      })}
    </OptionContainer>
  );
};

const OptionContainer = styled.div`
  display: flex;
  flex-wrap: wrap;

  /* Flex gap fix for safari */
  margin: -0.8rem 0 0 -0.8rem;

  > * {
    margin: 0.8rem 0 0 0.8rem;
  }
`;

export default OfferOptionContainer;
